import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axiosInstance from '@/api/axiosInstance'

export const useServices = (limit = 100) => {
    return useQuery({
        queryKey: ['services', limit],
        queryFn: async () => {
            const response = await axiosInstance.get(`/services/?limit=${limit}`)
            return response.data
        },
    })
}

export const useCategories = () => {
    return useQuery({
        queryKey: ['categories'],
        queryFn: async () => {
            try {
                const response = await axiosInstance.get('/categories/')
                if (response.data?.results && Array.isArray(response.data.results)) {
                    return response.data.results
                }
                if (Array.isArray(response.data)) {
                    return response.data
                }
                return []
            } catch (error) {
                console.error('Categories API error:', error)
                return []
            }
        },
    })
}

export const useServiceOfferings = (enabled: boolean = true) => {
    return useQuery({
        queryKey: ['service-offerings'],
        queryFn: async () => {
            const response = await axiosInstance.get('/service-offerings/my_offerings/')
            return response.data
        },
        enabled,
    })
}

export const useAvailableSchedule = (offeringId: number | null, startDate?: string, endDate?: string) => {
    return useQuery({
        queryKey: ['available-schedule', offeringId, startDate, endDate],
        queryFn: async () => {
            if (!offeringId) return null
            const params = new URLSearchParams()
            if (startDate) params.append('start_date', startDate)
            if (endDate) params.append('end_date', endDate)
            const response = await axiosInstance.get(`/service-offerings/${offeringId}/available_schedule/?${params.toString()}`)
            return response.data
        },
        enabled: !!offeringId,
        staleTime: 10 * 1000, // 10 seconds - refresh more frequently to catch new bookings
        refetchOnWindowFocus: true,
    })
}

export const useUpdateServiceOffering = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ offeringId, data }: { offeringId: number; data: any }) => {
            const response = await axiosInstance.patch(`/service-offerings/${offeringId}/`, data)
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['service-offerings'] })
            queryClient.invalidateQueries({ queryKey: ['provider-profile'] })
            queryClient.invalidateQueries({ queryKey: ['profile-status'] })
        },
    })
}

export const useDeleteServiceOffering = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (offeringId: number) => {
            const response = await axiosInstance.delete(`/service-offerings/${offeringId}/`)
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['service-offerings'] })
            queryClient.invalidateQueries({ queryKey: ['provider-profile'] })
            queryClient.invalidateQueries({ queryKey: ['profile-status'] })
        },
    })
}

export const useSelectServices = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data: any) => {
            const response = await axiosInstance.post('/provider-configuration/select_services/', data)
            return response.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['service-offerings'] })
            queryClient.invalidateQueries({ queryKey: ['provider-profile'] })
            queryClient.invalidateQueries({ queryKey: ['profile-status'] })
        },
    })
}

export const useNearbyServiceOfferings = (latitude?: number, longitude?: number, radius: number = 10) => {
    return useQuery({
        queryKey: ['nearby-service-offerings', latitude, longitude, radius],
        queryFn: async () => {
            if (!latitude || !longitude) return []
            const response = await axiosInstance.get('/service-offerings/nearby/', {
                params: { latitude, longitude, radius }
            })
            return response.data
        },
        enabled: !!latitude && !!longitude,
    })
}

export const useProviderFeed = (
    params?: {
        districtId?: number
        categoryId?: number
        searchQuery?: string
        page?: number
        pageSize?: number
        allProviders?: boolean
    },
    options?: {
        enabled?: boolean
    }
) => {
    return useQuery({
        queryKey: ['provider-feed', params],
        queryFn: async () => {
            const response = await axiosInstance.get('/services/provider_feed/', {
                params: {
                    district_id: params?.districtId,
                    category_id: params?.categoryId,
                    search: params?.searchQuery,
                    page: params?.page || 1,
                    page_size: params?.pageSize || 20,
                    all_providers: params?.allProviders ? 'true' : 'false',
                }
            })
            return response.data
        },
        enabled: options?.enabled !== undefined ? options.enabled : true,
    })
}

export const useDistricts = () => {
    return useQuery({
        queryKey: ['districts'],
        queryFn: async () => {
            const response = await axiosInstance.get('/districts/')
            if (Array.isArray(response.data)) {
                return response.data
            }
            if (response.data?.results) {
                return response.data.results
            }
            return []
        },
    })
}


